import { Button } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBulkUpdateDevices } from '~/api/devices';
import {
  Dialog,
  DialogContentTitle,
  DialogForm,
  DialogTitle,
} from '~/components/dialogs/components';
import { DialogActions, DialogContent } from '~/components/dialogs/lib/styles';
import type {
  DeviceBulkSelectionInput,
  DeviceMessagingBackend,
  DeviceNightlyBehavior,
} from '~/generated/graphql';
import type {
  DeviceList__Channel as Channel,
  DeviceList__DeviceGroup as DeviceGroup,
  DeviceList__TvBrand as TVBrand,
} from '../../queries/list.generated';
import { BulkUpdateConfirmDialog } from './BulkSettingsConfirmationDialog';
import {
  BulkDeviceSettingsForm,
  bulkDeviceSettingsValidationSchema,
  useBulkDeviceSettingsFormik,
} from './BulkSettingsForm';

interface BulkDeviceSettingsDialogProps {
  channels: ReadonlyArray<Channel>;
  deviceGroups: ReadonlyArray<DeviceGroup>;
  open: boolean;
  onClose: () => void;
  onComplete: () => void;
  selected: DeviceBulkSelectionInput;
  selectedDeviceCount: number;
  tvBrands: ReadonlyArray<TVBrand>;
}

export interface DeviceBulkPatchInput {
  api?: boolean;
  cacheContent?: boolean;
  closedCaptioning?: boolean;
  defaultChannelId?: number | null;
  demo?: boolean;
  deviceGroupId?: number | null;
  eventStream?: boolean;
  internal?: boolean;
  messagingBackend?: DeviceMessagingBackend | null;
  nightlyBehavior?: DeviceNightlyBehavior;
  overscan?: number;
  preload?: boolean;
  realtime?: boolean;
  screenshotInterval?: string | null;
  silence?: boolean;
  snmpService?: boolean;
  tvBrandId?: number | null;
}

export const BulkDeviceSettingsDialog = ({
  channels,
  deviceGroups,
  onClose,
  onComplete,
  open,
  selected,
  tvBrands,
}: BulkDeviceSettingsDialogProps) => {
  const [updateDevices, { loading }] = useBulkUpdateDevices();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { t } = useTranslation();

  const initialValues = {
    api: undefined,
    cacheContent: undefined,
    closedCaptioning: undefined,
    defaultChannelId: undefined,
    demo: undefined,
    deviceGroupId: undefined,
    eventStream: undefined,
    internal: undefined,
    messagingBackend: undefined,
    nightlyBehavior: undefined,
    overscan: undefined,
    preload: undefined,
    realtime: undefined,
    screenshotInterval: undefined,
    silence: undefined,
    snmpService: undefined,
    tvBrandId: undefined,
  };

  const formik = useBulkDeviceSettingsFormik(initialValues, () => {
    setConfirmOpen(true);
  });

  const patch = useMemo(() => {
    return bulkDeviceSettingsValidationSchema.cast(formik.values);
  }, [formik.values]);

  const handleClose = useCallback(() => {
    formik.resetForm();
    onClose();
  }, [formik, onClose]);

  return (
    <>
      <DialogForm
        onSubmit={(event) => {
          event.preventDefault();
          setConfirmOpen(true);
        }}
      >
        <Dialog maxWidth="sm" fullWidth onClose={handleClose} open={open}>
          <DialogTitle onClose={handleClose}>
            {t('pages.devices.bulk_actions.settings.title')}
          </DialogTitle>
          <DialogContent>
            <DialogContentTitle>
              {selected.ids?.length
                ? t('pages.devices.bulk_actions.settings.description', {
                    count: selected.ids.length,
                    thing: t('common.pluralize.device_plural', { count: selected.ids.length }),
                  })
                : t('pages.devices.bulk_actions.settings.description_all', {
                    thing: t('common.pluralize.device_plural', { count: 2 }),
                  })}
            </DialogContentTitle>
            <BulkDeviceSettingsForm
              formik={formik}
              channels={channels}
              deviceGroups={deviceGroups}
              tvBrands={tvBrands}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} variant="outlined" disabled={loading}>
              {t('common.dialogs.cancel')}
            </Button>
            <Button
              onClick={formik.submitForm}
              color="primary"
              variant="contained"
              disabled={loading || !formik.dirty || Object.keys(patch).length === 0}
            >
              {t('common.dialogs.update')}
            </Button>
          </DialogActions>
        </Dialog>
      </DialogForm>
      {confirmOpen && (
        <BulkUpdateConfirmDialog
          open={confirmOpen}
          onConfirm={async () => {
            try {
              await updateDevices({
                variables: {
                  input: {
                    patch,
                    selected,
                  },
                },
              });
              formik.resetForm();
              onComplete();
              onClose();
            } catch (error) {
              console.error(error);
            }
          }}
          onCancel={() => {
            setConfirmOpen(false);
            formik.setSubmitting(false);
          }}
          patch={patch}
          selectedDeviceCount={selected.ids ? selected.ids.length : 0}
          channels={channels}
          deviceGroups={deviceGroups}
          tvBrands={tvBrands}
          loading={loading}
        />
      )}
    </>
  );
};
